import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { 
  ArrowLeft, RefreshCw, TrendingUp, TrendingDown, DollarSign, Users, 
  Plus, Trash2, Edit, Save, X, ChevronRight, Plane, Bus, Calendar,
  Building, Utensils, Shield, Ticket, Receipt, MoreHorizontal, ChevronDown, ChevronUp,
  AlertCircle, CheckCircle, Clock, XCircle, CalendarX, Filter, Search
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EXPENSE_CATEGORIES = {
  hotel: { label: 'Hotel/Hospedagem', icon: Building, color: 'bg-purple-100 text-purple-800' },
  transporte: { label: 'Transporte', icon: Bus, color: 'bg-blue-100 text-blue-800' },
  guia: { label: 'Guia/Staff', icon: Users, color: 'bg-green-100 text-green-800' },
  alimentacao: { label: 'Alimentação', icon: Utensils, color: 'bg-orange-100 text-orange-800' },
  passeios: { label: 'Passeios/Ingressos', icon: Ticket, color: 'bg-pink-100 text-pink-800' },
  seguro: { label: 'Seguro', icon: Shield, color: 'bg-cyan-100 text-cyan-800' },
  taxa: { label: 'Taxas/Impostos', icon: Receipt, color: 'bg-red-100 text-red-800' },
  outros: { label: 'Outros', icon: MoreHorizontal, color: 'bg-gray-100 text-gray-800' }
};

const OPERATION_STATUS = {
  realizado: { label: 'Realizado', icon: CheckCircle, color: 'bg-green-100 text-green-800', iconColor: 'text-green-600' },
  aguardando_data: { label: 'Aguardando Data', icon: Clock, color: 'bg-yellow-100 text-yellow-800', iconColor: 'text-yellow-600' },
  adiado: { label: 'Adiado', icon: CalendarX, color: 'bg-orange-100 text-orange-800', iconColor: 'text-orange-600' },
  cancelado: { label: 'Cancelado', icon: XCircle, color: 'bg-red-100 text-red-800', iconColor: 'text-red-600' }
};

const AdminOperationalResults = () => {
  const navigate = useNavigate();
  const [operations, setOperations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOperationId, setSelectedOperationId] = useState('');
  const [operationDetails, setOperationDetails] = useState(null);
  const [breakeven, setBreakeven] = useState(null);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [operationStatus, setOperationStatus] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPostponeModal, setShowPostponeModal] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  
  // Estados para expandir DRE
  const [expandReceitas, setExpandReceitas] = useState(true);
  const [expandDespesas, setExpandDespesas] = useState(true);
  const [expandFixos, setExpandFixos] = useState(true);
  const [expandVariaveis, setExpandVariaveis] = useState(true);
  
  // Estados para filtros de despesas
  const [expenseFilter, setExpenseFilter] = useState('all');
  const [expenseSearch, setExpenseSearch] = useState('');
  const [expandExpenseCategories, setExpandExpenseCategories] = useState({});
  
  // Estados para edição de despesas
  const [editMode, setEditMode] = useState('orcado'); // 'orcado' ou 'realizado'
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteExpenseId, setDeleteExpenseId] = useState(null);
  const [showReplicateModal, setShowReplicateModal] = useState(false);
  const [replicateData, setReplicateData] = useState({ sourceOpId: '', newExpense: null });
  
  const [expenseForm, setExpenseForm] = useState({
    description: '',
    category: 'hotel',
    value: '',
    notes: '',
    status: 'projetado',
    payment_date: '',
    cost_type: 'fixo',
    cost_per_person: '',
    quantity: '',
    // Novos campos para forma de pagamento
    payment_method: 'pix', // 'pix' ou 'cartao'
    card_installments: 1, // Número de parcelas
    card_type: 'avista', // 'avista' ou 'prazo'
    // Campos para variação de despesa
    varies_by_client: false, // Varia por número de clientes
    varies_by_days: false, // Varia por número de dias
    // Campos para guias
    include_guides: 0, // 0, 1 ou 2 guias inclusos no custo variável
    guides_exempt_lodging: 0, // 0, 1 ou 2 guias isentos de hospedagem
    // Valores realizados (separados do orçado)
    actual_value: '',
    actual_quantity: ''
  });
  
  const [statusForm, setStatusForm] = useState({
    status: 'aguardando_data',
    budget_people: 0
  });

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin/login');
      return;
    }
    loadOperations();
    loadPackages();
  }, [navigate]);

  const loadOperations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/operations`);
      setOperations(response.data);
    } catch (error) {
      console.error('Erro ao carregar operações:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/packages/?active_only=false`);
      setPackages(response.data);
    } catch (error) {
      console.error('Erro ao carregar pacotes:', error);
    }
  };

  const loadOperationDetails = async (operationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/operations/${operationId}`);
      setOperationDetails(response.data);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
    }
  };

  // Calcular orçamento padrão baseado na categoria
  const getDefaultBudget = (category) => {
    if (!category) return 5;
    const cat = category.toLowerCase();
    // Aéreo (nacional ou internacional) = 5 pessoas
    if (cat.includes('aereo') || cat.includes('aéreo') || cat.includes('internacional')) {
      return 5;
    }
    // Rodoviário = 10 pessoas
    if (cat.includes('rodoviario') || cat.includes('rodoviário')) {
      return 10;
    }
    // Passeio 1 dia = 10 pessoas
    if (cat.includes('1-dia') || cat.includes('1dia') || cat.includes('passeio')) {
      return 10;
    }
    return 5; // Default
  };

  const loadOperationStatus = async (operationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/operation-status/${operationId}`);
      const statusData = response.data;
      
      // Calcular orçamento padrão se não tiver definido
      const op = operations.find(o => o.id === operationId);
      const defaultBudget = getDefaultBudget(op?.package_category);
      
      // Se não tem budget definido (0 ou undefined), salvar automaticamente o padrão
      if (!statusData.budget_people || statusData.budget_people === 0) {
        const newStatus = {
          ...statusData,
          status: statusData.status || 'aguardando_data',
          budget_people: defaultBudget
        };
        
        // Salvar automaticamente o orçamento padrão
        try {
          await axios.put(`${BACKEND_URL}/api/operational-results/operation-status/${operationId}`, newStatus);
          setOperationStatus(newStatus);
          setStatusForm(newStatus);
        } catch (saveError) {
          console.error('Erro ao salvar status padrão:', saveError);
          setOperationStatus(newStatus);
          setStatusForm(newStatus);
        }
      } else {
        setOperationStatus(statusData);
        setStatusForm({
          status: statusData.status || 'aguardando_data',
          budget_people: statusData.budget_people
        });
      }
    } catch (error) {
      console.error('Erro ao carregar status:', error);
      // Calcular e salvar orçamento padrão
      const op = operations.find(o => o.id === operationId);
      const defaultBudget = getDefaultBudget(op?.package_category);
      const newStatus = { status: 'aguardando_data', budget_people: defaultBudget };
      
      // Tentar salvar o padrão
      try {
        await axios.put(`${BACKEND_URL}/api/operational-results/operation-status/${operationId}`, newStatus);
      } catch (saveError) {
        console.error('Erro ao salvar status padrão:', saveError);
      }
      
      setOperationStatus(newStatus);
      setStatusForm(newStatus);
    }
  };

  // Lista de operações ordenada alfabeticamente
  const sortedOperations = useMemo(() => {
    return [...operations].sort((a, b) => {
      const nameA = a.package_name?.toLowerCase() || '';
      const nameB = b.package_name?.toLowerCase() || '';
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      return (a.date || '').localeCompare(b.date || '');
    });
  }, [operations]);

  // Função para extrair data de início do formato "03 a 09 de Setembro/2026"
  const parseDateLabel = (dateLabel) => {
    if (!dateLabel) return null;
    
    const monthNames = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    
    // Try to match "DD a DD de MMMM/YYYY" or "DD/MM a DD/MM/YYYY"
    const match1 = dateLabel.match(/(\d{1,2})\s*(?:a|até)\s*\d{1,2}\s*de\s*(\w+)\/(\d{4})/i);
    if (match1) {
      const day = parseInt(match1[1]);
      const monthIdx = monthNames.findIndex(m => m.toLowerCase() === match1[2].toLowerCase());
      const year = parseInt(match1[3]);
      if (monthIdx >= 0) {
        return new Date(year, monthIdx, day);
      }
    }
    
    // Try "DD/MM a DD/MM/YYYY"
    const match2 = dateLabel.match(/(\d{1,2})\/(\d{1,2})\s*a\s*\d{1,2}\/\d{1,2}\/(\d{4})/);
    if (match2) {
      const day = parseInt(match2[1]);
      const month = parseInt(match2[2]) - 1;
      const year = parseInt(match2[3]);
      return new Date(year, month, day);
    }
    
    return null;
  };

  // Agrupar pacotes por nome único (para o novo seletor)
  const uniquePackages = useMemo(() => {
    const packageMap = {};
    operations.forEach(op => {
      if (!packageMap[op.package_id]) {
        packageMap[op.package_id] = {
          id: op.package_id,
          name: op.package_name,
          category: op.package_category,
          dates: []
        };
      }
      packageMap[op.package_id].dates.push({
        operationId: op.id,
        dateId: op.date_id,
        dateLabel: op.date,
        parsedDate: parseDateLabel(op.date)
      });
    });
    
    // Ordenar datas de cada pacote: mais próxima primeiro
    Object.values(packageMap).forEach(pkg => {
      pkg.dates.sort((a, b) => {
        if (!a.parsedDate && !b.parsedDate) return 0;
        if (!a.parsedDate) return 1;
        if (!b.parsedDate) return -1;
        return a.parsedDate - b.parsedDate;
      });
    });
    
    return Object.values(packageMap).sort((a, b) => a.name.localeCompare(b.name));
  }, [operations]);

  // Estado para pacote selecionado (separado do operationId)
  const [selectedPackageId, setSelectedPackageId] = useState('');

  // Datas disponíveis para o pacote selecionado
  const availableDates = useMemo(() => {
    if (!selectedPackageId) return [];
    const pkg = uniquePackages.find(p => p.id === selectedPackageId);
    return pkg?.dates || [];
  }, [selectedPackageId, uniquePackages]);

  // Quando seleciona um pacote
  const handleSelectPackage = (packageId) => {
    setSelectedPackageId(packageId);
    setSelectedOperationId('');
    setOperationDetails(null);
    setBreakeven(null);
    setOperationStatus(null);
    
    // Se o pacote só tem uma data, seleciona automaticamente
    const pkg = uniquePackages.find(p => p.id === packageId);
    if (pkg?.dates?.length === 1) {
      handleSelectOperation(pkg.dates[0].operationId);
    }
  };

  const handleSelectOperation = async (operationId) => {
    setSelectedOperationId(operationId);
    if (operationId) {
      await Promise.all([
        loadOperationDetails(operationId),
        loadBreakeven(operationId),
        loadOperationStatus(operationId)
      ]);
    } else {
      setOperationDetails(null);
      setBreakeven(null);
      setOperationStatus(null);
    }
  };

  const loadBreakeven = async (operationId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/operational-results/breakeven/${operationId}`);
      setBreakeven(response.data);
    } catch (error) {
      console.error('Erro ao carregar ponto de equilíbrio:', error);
      setBreakeven(null);
    }
  };

  // Obter pacote selecionado
  const selectedOperation = operations.find(op => op.id === selectedOperationId);
  const currentPackage = useMemo(() => {
    if (!selectedOperationId) return null;
    const parts = selectedOperationId.split('_');
    const pkgId = parts[0];
    return packages.find(p => p.id === pkgId);
  }, [selectedOperationId, packages]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price || 0);
  };

  // Calcular quantidade de pessoas que já compraram
  const actualPeople = useMemo(() => {
    if (!operationDetails?.revenue?.by_room_type) return 0;
    let total = 0;
    Object.values(operationDetails.revenue.by_room_type).forEach(room => {
      total += room.people_count || 0;
    });
    return total;
  }, [operationDetails]);

  // Obter lista de clientes com suas categorias
  const customersList = useMemo(() => {
    if (!operationDetails?.revenue?.by_room_type) return [];
    const customers = [];
    Object.entries(operationDetails.revenue.by_room_type).forEach(([roomType, data]) => {
      (data.customers || []).forEach(customer => {
        customers.push({
          ...customer,
          roomType: roomType
        });
      });
    });
    return customers;
  }, [operationDetails]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      const baseValue = parseFloat(expenseForm.value) || 0;
      const qty = parseInt(expenseForm.quantity) || 1;
      const costPerPerson = parseFloat(expenseForm.cost_per_person) || parseFloat(expenseForm.value) || 0;

      let payload = {};

      if (editingExpense) {
        // EDITANDO despesa existente
        const isVariavel = editingExpense.cost_type === 'variavel';
        
        if (editMode === 'orcado') {
          // Editando ORÇADO - atualiza valores base
          // Se o realizado NÃO está pago, copia para realizado também
          const realizadoPago = editingExpense.status === 'pago';
          
          const valorBase = isVariavel ? costPerPerson : baseValue;
          
          payload = {
            ...editingExpense,
            description: expenseForm.description,
            category: expenseForm.category,
            value: valorBase,
            cost_per_person: isVariavel ? costPerPerson : editingExpense.cost_per_person,
            quantity: qty,
            cost_type: expenseForm.cost_type,
            varies_by_client: expenseForm.varies_by_client || false,
            // Guias - permitir reedição do orçado
            include_guides: isVariavel ? (expenseForm.include_guides || 0) : 0,
            guides_exempt_lodging: expenseForm.guides_exempt_lodging || 0,
            notes: expenseForm.notes || null,
            // Se não está pago, copia para realizado
            actual_value: realizadoPago ? editingExpense.actual_value : valorBase,
            actual_quantity: realizadoPago ? editingExpense.actual_quantity : qty
          };
        } else {
          // Editando REALIZADO - atualiza apenas valores realizados e status
          // Para modo realizado, o valor vem do campo "value" do formulário (não de cost_per_person)
          const valorRealizado = baseValue;
          
          payload = {
            ...editingExpense,
            status: expenseForm.status,
            payment_date: expenseForm.payment_date || null,
            payment_method: expenseForm.payment_method || 'pix',
            card_type: expenseForm.card_type || 'avista',
            card_installments: parseInt(expenseForm.card_installments) || 1,
            actual_value: valorRealizado,
            actual_quantity: qty
          };
        }
        
        await axios.put(`${BACKEND_URL}/api/operational-results/expenses/${editingExpense.id}`, payload);
      } else {
        // NOVA despesa
        const isVariavel = expenseForm.cost_type === 'variavel';
        const valorBase = isVariavel ? costPerPerson : baseValue;
        
        // CUSTO VARIÁVEL: automaticamente varia por cliente
        const variesByClient = isVariavel ? true : (expenseForm.varies_by_client || false);
        
        payload = {
          operation_id: selectedOperationId,
          description: expenseForm.description,
          category: expenseForm.category,
          value: valorBase,
          notes: expenseForm.notes || null,
          status: expenseForm.status,
          payment_date: expenseForm.payment_date || null,
          cost_type: expenseForm.cost_type,
          cost_per_person: isVariavel ? costPerPerson : 0,
          quantity: qty,
          varies_by_client: variesByClient,
          varies_by_days: expenseForm.varies_by_days || false,
          // Novos campos para guias
          include_guides: isVariavel ? (expenseForm.include_guides || 0) : 0,
          guides_exempt_lodging: expenseForm.guides_exempt_lodging || 0,
          payment_method: expenseForm.payment_method || 'pix',
          card_type: expenseForm.card_type || 'avista',
          card_installments: parseInt(expenseForm.card_installments) || 1,
          // Valores realizados iniciam iguais ao orçado
          actual_value: valorBase,
          actual_quantity: qty
        };
        
        await axios.post(`${BACKEND_URL}/api/operational-results/expenses`, payload);
        
        // Verificar se há outras datas do mesmo pacote para replicar
        if (currentPackage && currentPackage.dates && currentPackage.dates.length > 1) {
          const otherDates = currentPackage.dates.filter(d => {
            const opId = `${currentPackage.id}_${d.id}`;
            return opId !== selectedOperationId;
          });
          
          if (otherDates.length > 0) {
            setReplicateData({ 
              sourceOpId: selectedOperationId, 
              otherDates: otherDates.map(d => ({
                id: d.id,
                date: d.date,
                operationId: `${currentPackage.id}_${d.id}`
              }))
            });
            setShowReplicateModal(true);
          }
        }
      }
      
      setShowExpenseModal(false);
      resetExpenseForm();
      setEditMode('orcado');
      await loadOperationDetails(selectedOperationId);
      await loadBreakeven(selectedOperationId);
    } catch (error) {
      console.error('Erro ao salvar despesa:', error);
      alert('Erro ao salvar despesa. Verifique os campos e tente novamente.');
    }
  };

  const handleDeleteExpense = async (expenseId, deleteFuture = false) => {
    try {
      if (deleteFuture) {
        // Deletar esta e futuras parcelas
        await axios.delete(`${BACKEND_URL}/api/operational-results/expenses/${expenseId}?delete_future=true`);
      } else {
        await axios.delete(`${BACKEND_URL}/api/operational-results/expenses/${expenseId}`);
      }
      setShowDeleteModal(false);
      setDeleteExpenseId(null);
      await loadOperationDetails(selectedOperationId);
      await loadBreakeven(selectedOperationId);
    } catch (error) {
      console.error('Erro ao excluir despesa:', error);
    }
  };

  // Replicar despesas para outras datas do mesmo pacote
  const handleReplicateExpenses = async (targetOperationIds) => {
    try {
      if (!replicateData.sourceOpId || targetOperationIds.length === 0) return;
      
      await axios.post(`${BACKEND_URL}/api/operational-results/expenses/replicate`, {
        source_operation_id: replicateData.sourceOpId,
        target_operation_ids: targetOperationIds
      });
      
      setShowReplicateModal(false);
      setReplicateData({ sourceOpId: '', otherDates: [] });
      alert('Despesas replicadas com sucesso!');
    } catch (error) {
      console.error('Erro ao replicar despesas:', error);
      alert('Erro ao replicar despesas.');
    }
  };

  const handleUpdateStatus = async () => {
    try {
      if (statusForm.status === 'adiado') {
        setShowStatusModal(false);
        setShowPostponeModal(true);
        return;
      }
      
      await axios.put(`${BACKEND_URL}/api/operational-results/operation-status/${selectedOperationId}`, statusForm);
      await loadOperationStatus(selectedOperationId);
      setShowStatusModal(false);
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
    }
  };

  // Calcular data final baseada na data inicial e duração do pacote
  const calculateEndDate = (startDate) => {
    if (!startDate || !currentPackage?.duration_days) return '';
    // Parse date string YYYY-MM-DD to avoid timezone issues
    const [year, month, day] = startDate.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    const end = new Date(year, month - 1, day + currentPackage.duration_days - 1);
    // Return as YYYY-MM-DD
    return `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
  };

  const formatDateRange = (startDate) => {
    if (!startDate || !currentPackage?.duration_days) return '';
    // Parse date string YYYY-MM-DD to avoid timezone issues
    const [year, month, day] = startDate.split('-').map(Number);
    const start = new Date(year, month - 1, day);
    const end = new Date(year, month - 1, day + currentPackage.duration_days - 1);
    
    const formatDate = (d) => {
      return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
    };
    
    return `${formatDate(start)} a ${formatDate(end)}`;
  };

  const handleConfirmPostpone = async () => {
    try {
      if (!newStartDate) {
        alert('Selecione uma data de início');
        return;
      }

      const endDate = calculateEndDate(newStartDate);
      const formattedRange = formatDateRange(newStartDate);
      
      const payload = {
        ...statusForm,
        status: 'adiado',
        new_date: formattedRange,
        new_start_date: newStartDate,
        new_end_date: endDate
      };
      
      await axios.put(`${BACKEND_URL}/api/operational-results/operation-status/${selectedOperationId}`, payload);
      await loadOperationStatus(selectedOperationId);
      await loadOperations();
      await loadPackages();
      setShowPostponeModal(false);
      setNewStartDate('');
      alert(`Pacote adiado para: ${formattedRange}`);
    } catch (error) {
      console.error('Erro ao adiar operação:', error);
      alert('Erro ao adiar operação');
    }
  };

  const resetExpenseForm = () => {
    setEditingExpense(null);
    setExpenseForm({ 
      description: '', 
      category: 'hotel', 
      value: '', 
      notes: '',
      status: 'projetado',
      payment_date: '',
      cost_type: 'fixo',
      cost_per_person: '',
      quantity: '',
      payment_method: 'pix',
      card_installments: 1,
      card_type: 'avista',
      varies_by_client: false,
      varies_by_days: false,
      actual_value: '',
      actual_quantity: ''
    });
  };

  const getCategoryIcon = (category) => {
    const Icon = EXPENSE_CATEGORIES[category]?.icon || MoreHorizontal;
    return <Icon className="w-4 h-4" />;
  };

  const getRoomTypeLabel = (type) => {
    const labels = {
      'compartilhado': 'Compartilhado',
      'casal': 'Casal',
      'privativo': 'Privativo',
      'individual': 'Individual'
    };
    return labels[type] || type;
  };

  const getStatusBadge = (status) => {
    const config = OPERATION_STATUS[status] || OPERATION_STATUS.aguardando_data;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className={`w-3 h-3 ${config.iconColor}`} />
        {config.label}
      </Badge>
    );
  };

  // Agrupar despesas por categoria para o filtro
  const expensesByCategory = useMemo(() => {
    const expenses = operationDetails?.expenses?.items || [];
    const grouped = {};
    
    expenses.forEach(exp => {
      const cat = exp.category || 'outros';
      if (!grouped[cat]) {
        grouped[cat] = [];
      }
      grouped[cat].push(exp);
    });
    
    return grouped;
  }, [operationDetails]);

  // Filtrar despesas
  const filteredExpenses = useMemo(() => {
    let expenses = operationDetails?.expenses?.items || [];
    
    if (expenseFilter !== 'all') {
      expenses = expenses.filter(e => e.category === expenseFilter);
    }
    
    if (expenseSearch) {
      const search = expenseSearch.toLowerCase();
      expenses = expenses.filter(e => 
        e.description?.toLowerCase().includes(search) ||
        EXPENSE_CATEGORIES[e.category]?.label?.toLowerCase().includes(search)
      );
    }
    
    return expenses;
  }, [operationDetails, expenseFilter, expenseSearch]);

  // Calcular DRE detalhado com 7 colunas (Descrição, Orçado, %Orç, Realizado, %Real, Diferença, %)
  const dreDetailed = useMemo(() => {
    if (!operationDetails) return null;
    
    const budgetPeople = operationStatus?.budget_people || 0;
    
    // Usar preço médio do pacote se disponível, senão calcular do realizado
    let avgTicket = operationDetails.avg_package_price || 0;
    if (!avgTicket && operationDetails.revenue?.total && actualPeople > 0) {
      avgTicket = operationDetails.revenue.total / actualPeople;
    }
    
    const budgetRevenue = avgTicket * budgetPeople;
    const actualRevenue = operationDetails.revenue?.total || 0;
    
    // Separar despesas fixas e variáveis
    // LÓGICA CORRETA ORÇADO VS REALIZADO:
    // - Orçado = Todas as despesas (projetadas + pagas) - representa o valor planejado original
    // - Realizado = Despesas pagas + projetadas - PORÉM quando muda de projetado para pago,
    //   o valor SAI do Realizado (fica só no Orçado para comparação)
    // Na prática:
    // - Projetado: aparece em AMBAS as colunas (é o valor esperado)
    // - Pago: aparece APENAS no Realizado (o Orçado mantém o valor original projetado)
    const expenses = operationDetails.expenses?.items || [];
    const fixedExpenses = [];
    const variableExpenses = [];
    
    expenses.forEach(exp => {
      const isPaid = exp.status === 'pago';
      const qty = exp.quantity || 1;
      const actualQty = exp.actual_quantity || qty;
      
      // Função auxiliar para calcular valor total
      const calcTotal = (baseVal, qtd, multiplyByClients, clientCount) => {
        if (multiplyByClients) {
          return baseVal * qtd * clientCount;
        }
        return baseVal * qtd;
      };
      
      if (exp.cost_type === 'variavel') {
        const costPerPerson = exp.cost_per_person || exp.value || 0;
        const actualCostPerPerson = exp.actual_value || costPerPerson;
        
        // Considerar guias incluídos no custo variável
        const includeGuides = exp.include_guides || 0;
        const totalBudgetPeople = budgetPeople + includeGuides;
        const totalActualPeople = actualPeople + includeGuides;
        
        // Orçado: sempre usar valores orçados (custo variável SEMPRE multiplica por clientes + guias)
        const budgetVal = costPerPerson * qty * totalBudgetPeople;
        // Realizado: usar valores realizados se existirem
        const actualVal = actualCostPerPerson * actualQty * totalActualPeople;
        
        variableExpenses.push({
          ...exp,
          budgetValue: budgetVal,
          actualValue: actualVal
        });
      } else {
        // Custo fixo
        const baseValue = exp.value || 0;
        const actualBaseValue = exp.actual_value || baseValue;
        
        // Verificar guias isentos (para hospedagem)
        const guidesExempt = exp.guides_exempt_lodging || 0;
        const effectiveBudgetPeople = exp.varies_by_client ? Math.max(0, budgetPeople - guidesExempt) : 1;
        const effectiveActualPeople = exp.varies_by_client ? Math.max(0, actualPeople - guidesExempt) : 1;
        
        // Orçado: valor base × quantidade × (clientes - guias isentos se varies_by_client)
        const budgetVal = baseValue * qty * effectiveBudgetPeople;
        // Realizado: valor realizado × quantidade realizada × (clientes - guias isentos se varies_by_client)
        const actualVal = actualBaseValue * actualQty * effectiveActualPeople;
        
        fixedExpenses.push({
          ...exp,
          budgetValue: budgetVal,
          actualValue: actualVal
        });
      }
    });
    
    const totalFixedBudget = fixedExpenses.reduce((sum, e) => sum + e.budgetValue, 0);
    const totalFixedActual = fixedExpenses.reduce((sum, e) => sum + e.actualValue, 0);
    const totalVariableBudget = variableExpenses.reduce((sum, e) => sum + e.budgetValue, 0);
    const totalVariableActual = variableExpenses.reduce((sum, e) => sum + e.actualValue, 0);
    
    const totalExpensesBudget = totalFixedBudget + totalVariableBudget;
    const totalExpensesActual = totalFixedActual + totalVariableActual;
    
    const resultBudget = budgetRevenue - totalExpensesBudget;
    const resultActual = actualRevenue - totalExpensesActual;
    
    // Calcular % de cada item em relação ao total
    const calcPct = (value, total) => total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
    
    return {
      budgetPeople,
      actualPeople,
      avgTicket,
      customersList,
      receitas: {
        budget: budgetRevenue,
        budgetPct: '100.0',
        actual: actualRevenue,
        actualPct: '100.0',
        diff: actualRevenue - budgetRevenue,
        diffPct: budgetRevenue > 0 ? ((actualRevenue / budgetRevenue) * 100 - 100).toFixed(1) : '0.0'
      },
      despesas: {
        fixas: {
          items: fixedExpenses.map(e => ({
            ...e,
            budgetPct: calcPct(e.budgetValue, totalFixedBudget),
            actualPct: calcPct(e.actualValue, totalFixedActual)
          })),
          budget: totalFixedBudget,
          budgetPct: calcPct(totalFixedBudget, totalExpensesBudget),
          actual: totalFixedActual,
          actualPct: calcPct(totalFixedActual, totalExpensesActual),
          diff: totalFixedBudget - totalFixedActual,
          diffPct: totalFixedBudget > 0 ? ((totalFixedActual / totalFixedBudget) * 100 - 100).toFixed(1) : '0.0'
        },
        variaveis: {
          items: variableExpenses.map(e => ({
            ...e,
            budgetPct: calcPct(e.budgetValue, totalVariableBudget),
            actualPct: calcPct(e.actualValue, totalVariableActual)
          })),
          budget: totalVariableBudget,
          budgetPct: calcPct(totalVariableBudget, totalExpensesBudget),
          actual: totalVariableActual,
          actualPct: calcPct(totalVariableActual, totalExpensesActual),
          diff: totalVariableBudget - totalVariableActual,
          diffPct: totalVariableBudget > 0 ? ((totalVariableActual / totalVariableBudget) * 100 - 100).toFixed(1) : '0.0'
        },
        total: {
          budget: totalExpensesBudget,
          budgetPct: budgetRevenue > 0 ? calcPct(totalExpensesBudget, budgetRevenue) : '0.0',
          actual: totalExpensesActual,
          actualPct: actualRevenue > 0 ? calcPct(totalExpensesActual, actualRevenue) : '0.0',
          diff: totalExpensesBudget - totalExpensesActual,
          diffPct: totalExpensesBudget > 0 ? ((totalExpensesActual / totalExpensesBudget) * 100 - 100).toFixed(1) : '0.0'
        }
      },
      resultado: {
        budget: resultBudget,
        budgetPct: budgetRevenue > 0 ? calcPct(resultBudget, budgetRevenue) : '0.0',
        actual: resultActual,
        actualPct: actualRevenue > 0 ? calcPct(resultActual, actualRevenue) : '0.0',
        diff: resultActual - resultBudget,
        diffPct: Math.abs(resultBudget) > 0 ? ((resultActual / Math.abs(resultBudget)) * 100 - 100).toFixed(1) : '0.0'
      }
    };
  }, [operationDetails, operationStatus, actualPeople, customersList]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#003366] text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigate('/admin')} className="flex items-center space-x-2 hover:opacity-80">
              <ArrowLeft className="w-5 h-5" />
              <span>Voltar</span>
            </button>
            <div className="h-6 w-px bg-white/30"></div>
            <div className="flex items-center space-x-2">
              <TrendingUp className="w-6 h-6" />
              <h1 className="text-xl font-bold">Resultados Operacionais</h1>
            </div>
          </div>
          <Button onClick={loadOperations} variant="outline" size="sm" className="text-white border-white hover:bg-white hover:text-[#003366]">
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Operation Selector - PACOTE + DATA separados */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <Label className="font-semibold whitespace-nowrap">Selecione a Operação:</Label>
              
              {/* Seletor de PACOTE (apenas nome) */}
              <Select value={selectedPackageId} onValueChange={handleSelectPackage}>
                <SelectTrigger className="w-[300px]">
                  <SelectValue placeholder="Escolha o pacote..." />
                </SelectTrigger>
                <SelectContent>
                  {uniquePackages.map(pkg => (
                    <SelectItem key={pkg.id} value={pkg.id}>
                      <div className="flex items-center gap-2">
                        {(pkg.category?.toLowerCase().includes('aereo') || pkg.category?.toLowerCase().includes('aéreo') || pkg.category === 'internacional') ? (
                          <Plane className="w-4 h-4 text-blue-600" />
                        ) : (
                          <Bus className="w-4 h-4 text-green-600" />
                        )}
                        <span>{pkg.name}</span>
                        {pkg.dates.length > 1 && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {pkg.dates.length} datas
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Seletor de DATA (apenas quando pacote tem mais de 1 data) */}
              {selectedPackageId && availableDates.length > 1 && (
                <Select value={selectedOperationId} onValueChange={handleSelectOperation}>
                  <SelectTrigger className="w-[250px]">
                    <SelectValue placeholder="Escolha a data..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableDates.map(date => (
                      <SelectItem key={date.operationId} value={date.operationId}>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>{date.dateLabel}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#003366]"></div>
          </div>
        ) : !selectedOperationId ? (
          <Card>
            <CardContent className="p-8 text-center">
              <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Selecione uma Operação</h3>
              <p className="text-gray-600">Escolha um pacote e data no dropdown acima para ver os resultados.</p>
            </CardContent>
          </Card>
        ) : !operationDetails ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#003366]"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Header Card com Status */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      {(operationDetails.package_category?.toLowerCase().includes('aereo') || operationDetails.package_category?.toLowerCase().includes('aéreo') || operationDetails.package_category === 'internacional') ? (
                        <Plane className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Bus className="w-5 h-5 text-green-600" />
                      )}
                      {operationDetails.package_name}
                    </h2>
                    <p className="text-gray-600 flex items-center gap-1 mt-1">
                      <Calendar className="w-4 h-4" />
                      {operationDetails.date}
                      {currentPackage?.duration_days && (
                        <span className="text-sm text-gray-500 ml-2">({currentPackage.duration_days} dias)</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(operationStatus?.status || 'aguardando_data')}
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowStatusModal(true)}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Configurar
                    </Button>
                  </div>
                </div>
                
                {/* Info de Pessoas */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-xs text-blue-600 font-medium">Pessoas Orçadas</p>
                    <p className="text-xl font-bold text-blue-800">{operationStatus?.budget_people || 0}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-xs text-green-600 font-medium">Pessoas Vendidas</p>
                    <p className="text-xl font-bold text-green-800">{actualPeople}</p>
                  </div>
                  <div className="bg-purple-50 p-3 rounded-lg">
                    <p className="text-xs text-purple-600 font-medium">Ticket Médio</p>
                    <p className="text-xl font-bold text-purple-800">{formatPrice(dreDetailed?.avgTicket || 0)}</p>
                  </div>
                  <div className={`p-3 rounded-lg ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <p className={`text-xs font-medium ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      Resultado Atual
                    </p>
                    <p className={`text-xl font-bold ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                      {formatPrice(dreDetailed?.resultado?.actual || 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DRE com 7 colunas */}
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  DRE - Demonstração do Resultado
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#003366] text-white">
                        <th className="p-3 text-left font-semibold">Descrição</th>
                        <th className="p-3 text-right font-semibold">Orçado ({operationStatus?.budget_people || 0}p)</th>
                        <th className="p-3 text-right font-semibold">%</th>
                        <th className="p-3 text-right font-semibold">Realizado ({actualPeople}p)</th>
                        <th className="p-3 text-right font-semibold">%</th>
                        <th className="p-3 text-right font-semibold">Diferença</th>
                        <th className="p-3 text-right font-semibold">Var%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* RECEITAS */}
                      <tr 
                        className="bg-green-100 cursor-pointer hover:bg-green-200"
                        onClick={() => setExpandReceitas(!expandReceitas)}
                      >
                        <td className="p-3 font-bold flex items-center gap-2">
                          {expandReceitas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          RECEITAS
                        </td>
                        <td className="p-3 text-right font-bold text-green-700">{formatPrice(dreDetailed?.receitas?.budget)}</td>
                        <td className="p-3 text-right text-green-700">{dreDetailed?.receitas?.budgetPct}%</td>
                        <td className="p-3 text-right font-bold text-green-700">{formatPrice(dreDetailed?.receitas?.actual)}</td>
                        <td className="p-3 text-right text-green-700">{dreDetailed?.receitas?.actualPct}%</td>
                        <td className={`p-3 text-right font-bold ${(dreDetailed?.receitas?.diff || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatPrice(dreDetailed?.receitas?.diff)}
                        </td>
                        <td className={`p-3 text-right font-bold ${parseFloat(dreDetailed?.receitas?.diffPct || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {dreDetailed?.receitas?.diffPct}%
                        </td>
                      </tr>
                      
                      {expandReceitas && (
                        <>
                          {/* Orçado - Linha simplificada */}
                          <tr className="bg-green-50">
                            <td className="p-2 pl-8 text-gray-600">Orçamento</td>
                            <td className="p-2 text-right text-gray-600">
                              {operationStatus?.budget_people || 0} pacotes
                            </td>
                            <td className="p-2 text-right">-</td>
                            <td className="p-2 text-right">-</td>
                            <td className="p-2 text-right">-</td>
                            <td className="p-2 text-right">-</td>
                            <td className="p-2 text-right">-</td>
                          </tr>
                          
                          {/* Realizado - Lista de clientes */}
                          {customersList.map((customer, idx) => {
                            // Obter travelers que não são o principal (dependentes)
                            const dependents = (customer.travelers || []).filter(t => {
                              const tName = (t.name || '').trim().toLowerCase();
                              const cName = (customer.name || '').trim().toLowerCase();
                              return tName && tName !== cName;
                            });
                            
                            const reservationUrl = customer.is_manual 
                              ? `/admin/reservas-manuais` 
                              : `/admin/reservas`;
                            
                            return (
                              <React.Fragment key={idx}>
                                <tr className="bg-white hover:bg-gray-50">
                                  <td className="p-2 pl-8 text-gray-600">
                                    <div className="flex items-center gap-2">
                                      <Users className="w-3 h-3 text-gray-400" />
                                      <span 
                                        className="cursor-pointer hover:text-blue-600 hover:underline font-medium"
                                        onClick={() => navigate(reservationUrl)}
                                        title={`Ir para ${customer.is_manual ? 'reserva manual' : 'reserva'}`}
                                        data-testid={`customer-link-${idx}`}
                                      >
                                        {customer.name}
                                      </span>
                                      {customer.quantity > 1 && (
                                        <Badge className="text-[10px] bg-blue-50 text-blue-600">
                                          {customer.quantity}p
                                        </Badge>
                                      )}
                                      <Badge className="text-xs bg-gray-100 text-gray-600">
                                        {getRoomTypeLabel(customer.roomType)}
                                      </Badge>
                                      {customer.is_manual && (
                                        <Badge className="text-xs bg-yellow-100 text-yellow-700">Manual</Badge>
                                      )}
                                    </div>
                                    {/* Dependentes / Travelers */}
                                    {dependents.length > 0 && (
                                      <div className="ml-7 mt-1 space-y-0.5">
                                        {dependents.map((dep, dIdx) => (
                                          <div key={dIdx} className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <span className="text-gray-300">└</span>
                                            <span>{dep.name}</span>
                                            <span className="text-gray-400">(acompanhante)</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </td>
                                  <td className="p-2 text-right">-</td>
                                  <td className="p-2 text-right">-</td>
                                  <td className="p-2 text-right text-gray-600">{formatPrice(customer.amount)}</td>
                                  <td className="p-2 text-right text-gray-500 text-xs">
                                    {dreDetailed?.receitas?.actual > 0 
                                      ? ((customer.amount / dreDetailed.receitas.actual) * 100).toFixed(1) 
                                      : 0}%
                                  </td>
                                  <td className="p-2 text-right">-</td>
                                  <td className="p-2 text-right">-</td>
                                </tr>
                              </React.Fragment>
                            );
                          })}
                        </>
                      )}
                      
                      {/* DESPESAS */}
                      <tr 
                        className="bg-red-100 cursor-pointer hover:bg-red-200"
                        onClick={() => setExpandDespesas(!expandDespesas)}
                      >
                        <td className="p-3 font-bold flex items-center gap-2">
                          {expandDespesas ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          DESPESAS OPERACIONAIS
                        </td>
                        <td className="p-3 text-right font-bold text-red-700">{formatPrice(dreDetailed?.despesas?.total?.budget)}</td>
                        <td className="p-3 text-right text-red-700">{dreDetailed?.despesas?.total?.budgetPct}%</td>
                        <td className="p-3 text-right font-bold text-red-700">{formatPrice(dreDetailed?.despesas?.total?.actual)}</td>
                        <td className="p-3 text-right text-red-700">{dreDetailed?.despesas?.total?.actualPct}%</td>
                        <td className={`p-3 text-right font-bold ${(dreDetailed?.despesas?.total?.diff || 0) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatPrice(dreDetailed?.despesas?.total?.diff)}
                        </td>
                        <td className={`p-3 text-right font-bold ${parseFloat(dreDetailed?.despesas?.total?.diffPct || 0) <= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {dreDetailed?.despesas?.total?.diffPct}%
                        </td>
                      </tr>
                      
                      {expandDespesas && (
                        <>
                          {/* Custos Fixos */}
                          <tr 
                            className="bg-gray-100 cursor-pointer hover:bg-gray-200"
                            onClick={(e) => { e.stopPropagation(); setExpandFixos(!expandFixos); }}
                          >
                            <td className="p-2 pl-6 font-medium flex items-center gap-2">
                              {expandFixos ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              📌 Custos Fixos
                            </td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.fixas?.budget)}</td>
                            <td className="p-2 text-right text-gray-600">{dreDetailed?.despesas?.fixas?.budgetPct}%</td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.fixas?.actual)}</td>
                            <td className="p-2 text-right text-gray-600">{dreDetailed?.despesas?.fixas?.actualPct}%</td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.fixas?.diff)}</td>
                            <td className="p-2 text-right">{dreDetailed?.despesas?.fixas?.diffPct}%</td>
                          </tr>
                          
                          {expandFixos && dreDetailed?.despesas?.fixas?.items?.map((exp, idx) => (
                            <tr key={`fixed-${idx}`} className="bg-white hover:bg-gray-50">
                              <td className="p-2 pl-12 text-gray-600">{exp.description}</td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.budgetValue)}</td>
                              <td className="p-2 text-right text-gray-500 text-xs">{exp.budgetPct}%</td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.actualValue)}</td>
                              <td className="p-2 text-right text-gray-500 text-xs">{exp.actualPct}%</td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.budgetValue - exp.actualValue)}</td>
                              <td className="p-2 text-right text-gray-600">-</td>
                            </tr>
                          ))}
                          
                          {/* Custos Variáveis */}
                          <tr 
                            className="bg-gray-100 cursor-pointer hover:bg-gray-200"
                            onClick={(e) => { e.stopPropagation(); setExpandVariaveis(!expandVariaveis); }}
                          >
                            <td className="p-2 pl-6 font-medium flex items-center gap-2">
                              {expandVariaveis ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              📊 Custos Variáveis (×{actualPeople} pessoas)
                            </td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.variaveis?.budget)}</td>
                            <td className="p-2 text-right text-gray-600">{dreDetailed?.despesas?.variaveis?.budgetPct}%</td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.variaveis?.actual)}</td>
                            <td className="p-2 text-right text-gray-600">{dreDetailed?.despesas?.variaveis?.actualPct}%</td>
                            <td className="p-2 text-right">{formatPrice(dreDetailed?.despesas?.variaveis?.diff)}</td>
                            <td className="p-2 text-right">{dreDetailed?.despesas?.variaveis?.diffPct}%</td>
                          </tr>
                          
                          {expandVariaveis && dreDetailed?.despesas?.variaveis?.items?.map((exp, idx) => (
                            <tr key={`var-${idx}`} className="bg-white hover:bg-gray-50">
                              <td className="p-2 pl-12 text-gray-600">
                                {exp.description}
                                <span className="text-xs text-blue-600 ml-2">
                                  ({formatPrice(exp.cost_per_person)}/p × {exp.quantity || 1})
                                </span>
                              </td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.budgetValue)}</td>
                              <td className="p-2 text-right text-gray-500 text-xs">{exp.budgetPct}%</td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.actualValue)}</td>
                              <td className="p-2 text-right text-gray-500 text-xs">{exp.actualPct}%</td>
                              <td className="p-2 text-right text-gray-600">{formatPrice(exp.budgetValue - exp.actualValue)}</td>
                              <td className="p-2 text-right text-gray-600">-</td>
                            </tr>
                          ))}
                        </>
                      )}
                      
                      {/* RESULTADO */}
                      <tr className={`font-bold text-lg ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'bg-blue-200' : 'bg-red-200'}`}>
                        <td className="p-3">RESULTADO OPERACIONAL</td>
                        <td className={`p-3 text-right ${(dreDetailed?.resultado?.budget || 0) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                          {formatPrice(dreDetailed?.resultado?.budget)}
                        </td>
                        <td className={`p-3 text-right ${(dreDetailed?.resultado?.budget || 0) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                          {dreDetailed?.resultado?.budgetPct}%
                        </td>
                        <td className={`p-3 text-right ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                          {formatPrice(dreDetailed?.resultado?.actual)}
                        </td>
                        <td className={`p-3 text-right ${(dreDetailed?.resultado?.actual || 0) >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
                          {dreDetailed?.resultado?.actualPct}%
                        </td>
                        <td className={`p-3 text-right ${(dreDetailed?.resultado?.diff || 0) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                          {formatPrice(dreDetailed?.resultado?.diff)}
                        </td>
                        <td className={`p-3 text-right ${parseFloat(dreDetailed?.resultado?.diffPct || 0) >= 0 ? 'text-green-800' : 'text-red-800'}`}>
                          {dreDetailed?.resultado?.diffPct}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Gerenciar Despesas - Com filtros e consolidação */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <TrendingDown className="w-5 h-5 text-red-600" />
                    Gerenciar Despesas ({formatPrice(operationDetails.expenses?.total || 0)})
                  </h3>
                  <Button 
                    size="sm" 
                    onClick={() => { resetExpenseForm(); setShowExpenseModal(true); }}
                    className="bg-[#003366] hover:bg-[#002244]"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Despesa
                  </Button>
                </div>
                
                {/* Filtros */}
                <div className="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <Select value={expenseFilter} onValueChange={setExpenseFilter}>
                      <SelectTrigger className="w-[180px] h-8">
                        <SelectValue placeholder="Filtrar categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas Categorias</SelectItem>
                        {Object.entries(EXPENSE_CATEGORIES).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-1">
                    <Search className="w-4 h-4 text-gray-500" />
                    <Input
                      type="text"
                      placeholder="Buscar despesa..."
                      value={expenseSearch}
                      onChange={(e) => setExpenseSearch(e.target.value)}
                      className="h-8 max-w-xs"
                    />
                  </div>
                  
                  <div className="text-sm text-gray-600">
                    {filteredExpenses.length} despesa(s) • Total: {formatPrice(filteredExpenses.reduce((s, e) => s + (e.value || 0), 0))}
                  </div>
                </div>
                
                {/* Lista de despesas agrupada por categoria */}
                {expenseFilter === 'all' ? (
                  // Modo consolidado por categoria
                  <div className="space-y-3">
                    {Object.entries(expensesByCategory).map(([category, expenses]) => {
                      const categoryTotal = expenses.reduce((s, e) => s + (e.value || 0), 0);
                      const isExpanded = expandExpenseCategories[category];
                      
                      return (
                        <div key={category} className="border rounded-lg overflow-hidden">
                          <div 
                            className={`flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 ${EXPENSE_CATEGORIES[category]?.color || 'bg-gray-100'}`}
                            onClick={() => setExpandExpenseCategories(prev => ({ ...prev, [category]: !prev[category] }))}
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              {getCategoryIcon(category)}
                              <span className="font-medium">{EXPENSE_CATEGORIES[category]?.label || category}</span>
                              <Badge className="bg-white/50">{expenses.length}</Badge>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="bg-white">
                              {/* Header da tabela de despesas - alinhado com DRE */}
                              <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-100 text-xs font-semibold text-gray-600 border-b">
                                <div className="col-span-5">Descrição</div>
                                <div className="col-span-2 text-center">Status</div>
                                <div className="col-span-2 text-right">Orçado</div>
                                <div className="col-span-2 text-right">Realizado</div>
                                <div className="col-span-1 text-center">Ações</div>
                              </div>
                              
                              {expenses.map((expense) => {
                                // Calcular valores para exibição
                                const baseVal = expense.cost_type === 'variavel' ? (expense.cost_per_person || 0) : (expense.value || 0);
                                const qty = expense.quantity || 1;
                                const bPeople = dreDetailed?.budgetPeople || 0;
                                const aPeople = dreDetailed?.actualPeople || actualPeople || 0;
                                const includeGuides = expense.include_guides || 0;
                                const guidesExempt = expense.guides_exempt_lodging || 0;
                                // Custo variável: clientes + guias inclusos
                                // Custo fixo + varies_by_client: clientes - guias isentos (hospedagem)
                                const budgetMultiplier = expense.cost_type === 'variavel'
                                  ? (bPeople + includeGuides)
                                  : (expense.varies_by_client ? Math.max(0, bPeople - guidesExempt) : 1);
                                const actualMultiplier = expense.cost_type === 'variavel'
                                  ? (aPeople + includeGuides)
                                  : (expense.varies_by_client ? Math.max(0, aPeople - guidesExempt) : 1);
                                const budgetTotal = baseVal * qty * budgetMultiplier;

                                const actualBaseVal = expense.actual_value || baseVal;
                                const actualQty = expense.actual_quantity || qty;
                                const actualTotal = actualBaseVal * actualQty * actualMultiplier;

                                const isRealizadoPago = expense.status === 'pago';

                                return (
                                  <div key={expense.id} className="grid grid-cols-12 gap-2 px-3 py-2 border-b hover:bg-gray-50 items-center text-sm">
                                    {/* Descrição */}
                                    <div className="col-span-5">
                                      <p className="font-medium truncate">{expense.description}</p>
                                      <p className="text-xs text-gray-500">
                                        {`${formatPrice(baseVal)} × ${qty}`}
                                        {expense.cost_type === 'variavel' && (
                                          <>
                                            {` × ${budgetMultiplier}p`}
                                            {includeGuides > 0 && (
                                              <span className="text-green-600 font-medium"> ({bPeople}c+{includeGuides}g)</span>
                                            )}
                                          </>
                                        )}
                                        {expense.cost_type !== 'variavel' && expense.varies_by_client && (
                                          <>
                                            {` × ${budgetMultiplier}p`}
                                            {guidesExempt > 0 && (
                                              <span className="text-amber-600 font-medium"> ({bPeople}c-{guidesExempt}g)</span>
                                            )}
                                          </>
                                        )}
                                      </p>
                                    </div>
                                    
                                    {/* Status */}
                                    <div className="col-span-2 text-center">
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        isRealizadoPago 
                                          ? 'bg-green-100 text-green-700' 
                                          : 'bg-orange-100 text-orange-700'
                                      }`}>
                                        {isRealizadoPago ? 'Pago' : 'Previsto'}
                                      </span>
                                    </div>
                                    
                                    {/* Orçado + botão editar */}
                                    <div className="col-span-2 text-right flex items-center justify-end gap-1">
                                      <span className="text-orange-600 font-medium">{formatPrice(budgetTotal)}</span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingExpense(expense);
                                          setEditMode('orcado');
                                          setExpenseForm({...expense});
                                          setShowExpenseModal(true);
                                        }}
                                        className="p-1 hover:bg-orange-100 rounded text-orange-600"
                                        title="Editar Orçado"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                    </div>
                                    
                                    {/* Realizado + botão editar */}
                                    <div className="col-span-2 text-right flex items-center justify-end gap-1">
                                      <span className={`font-medium ${isRealizadoPago ? 'text-green-600' : 'text-gray-600'}`}>
                                        {formatPrice(actualTotal)}
                                      </span>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setEditingExpense(expense);
                                          setEditMode('realizado');
                                          setExpenseForm({
                                            ...expense,
                                            value: expense.actual_value || expense.value,
                                            quantity: expense.actual_quantity || expense.quantity
                                          });
                                          setShowExpenseModal(true);
                                        }}
                                        className="p-1 hover:bg-green-100 rounded text-green-600"
                                        title="Editar Realizado"
                                      >
                                        <Edit className="w-3 h-3" />
                                      </button>
                                    </div>
                                    
                                    {/* Ações */}
                                    <div className="col-span-1 text-center">
                                      <button
                                        onClick={(e) => { 
                                          e.stopPropagation(); 
                                          if (expense.is_recurring) {
                                            setDeleteExpenseId(expense.id);
                                            setShowDeleteModal(true);
                                          } else {
                                            handleDeleteExpense(expense.id); 
                                          }
                                        }}
                                        className="p-1 hover:bg-red-100 rounded text-red-600"
                                        title="Excluir"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  // Modo lista filtrada
                  <div className="space-y-2">
                    {filteredExpenses.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">Nenhuma despesa encontrada.</p>
                    ) : (
                      filteredExpenses.map((expense) => (
                        <div key={expense.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 flex-1">
                            <div className={`p-1.5 rounded ${EXPENSE_CATEGORIES[expense.category]?.color || 'bg-gray-100'}`}>
                              {getCategoryIcon(expense.category)}
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-sm">{expense.description}</p>
                              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                <span>{EXPENSE_CATEGORIES[expense.category]?.label}</span>
                                <span>•</span>
                                <span className={expense.cost_type === 'variavel' ? 'text-blue-600 font-medium' : ''}>
                                  {expense.cost_type === 'variavel' ? `📊 ${formatPrice(expense.cost_per_person)}/p × ${expense.quantity || 1} × ${actualPeople}p` : '📌 Fixo'}
                                </span>
                                <span>•</span>
                                <span className={expense.status === 'pago' ? 'text-green-600' : 'text-orange-600'}>
                                  {expense.status === 'pago' ? '✅ Pago' : '📋 Projetado'}
                                </span>
                                {expense.varies_by_client && (
                                  <span className="text-purple-600">👥 Varia/cliente</span>
                                )}
                                {expense.varies_by_days && (
                                  <span className="text-purple-600">📅 Varia/dia</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-red-600 font-medium">
                              {expense.cost_type === 'variavel' 
                                ? formatPrice((expense.cost_per_person || 0) * (expense.quantity || 1) * actualPeople)
                                : formatPrice(expense.value)
                              }
                            </span>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0"
                              onClick={() => {
                                setEditingExpense(expense);
                                setExpenseForm({...expense});
                                setShowExpenseModal(true);
                              }}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600"
                              onClick={() => handleDeleteExpense(expense.id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Ponto de Equilíbrio */}
            {breakeven && (
              <Card className="border-2 border-blue-200">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                    Ponto de Equilíbrio
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Custos Fixos</p>
                      <p className="text-lg font-bold text-gray-800">{formatPrice(breakeven.costs?.fixed_total)}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-xs text-gray-500">Custo Variável/Pessoa</p>
                      <p className="text-lg font-bold text-gray-800">{formatPrice(breakeven.costs?.variable_per_person)}</p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">Breakeven (Pessoas)</p>
                      <p className="text-lg font-bold text-blue-700">
                        {Math.ceil(breakeven.breakeven?.people || 0)} pessoas
                      </p>
                    </div>
                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">Breakeven (R$)</p>
                      <p className="text-lg font-bold text-blue-700">{formatPrice(breakeven.breakeven?.revenue)}</p>
                    </div>
                  </div>
                  
                  <div className={`p-4 rounded-lg ${breakeven.current_situation?.above_breakeven ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">Situação Atual</p>
                        <p className="text-lg font-bold">
                          {breakeven.current_situation?.people} / {Math.ceil(breakeven.breakeven?.people || 0)} pessoas
                        </p>
                      </div>
                      <div className="text-right">
                        {breakeven.current_situation?.above_breakeven ? (
                          <div className="text-green-700">
                            <p className="text-sm">✅ Acima do breakeven</p>
                            <p className="font-bold">Lucro: {formatPrice(breakeven.current_situation?.profit)}</p>
                          </div>
                        ) : (
                          <div className="text-orange-700">
                            <p className="text-sm">⚠️ Abaixo do breakeven</p>
                            <p className="font-bold">Faltam {Math.ceil(breakeven.current_situation?.people_to_breakeven || 0)} pessoas</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-gray-500 mt-3">
                    Margem de contribuição: {formatPrice(breakeven.breakeven?.contribution_margin)}/pessoa
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Expense Modal */}
      <Dialog open={showExpenseModal} onOpenChange={(open) => { setShowExpenseModal(open); if (!open) setEditMode('orcado'); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingExpense 
                ? (editMode === 'realizado' 
                    ? '✏️ Editar Realizado' 
                    : '✏️ Editar Orçado')
                : '➕ Nova Despesa'}
            </DialogTitle>
            {editingExpense && editMode === 'orcado' && (
              <p className="text-sm text-orange-600">
                ⚠️ Alterações no orçado serão copiadas para o realizado (exceto se já estiver pago)
              </p>
            )}
            {editingExpense && editMode === 'realizado' && (
              <p className="text-sm text-green-600">
                💰 Editando apenas os valores realizados e status de pagamento
              </p>
            )}
          </DialogHeader>
          
          <form onSubmit={handleAddExpense} className="space-y-4">
            {/* Se editando REALIZADO, mostrar apenas campos de valor e status */}
            {editingExpense && editMode === 'realizado' ? (
              <>
                {/* Info da despesa (somente leitura) */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{editingExpense.description}</p>
                  <p className="text-sm text-gray-500">{EXPENSE_CATEGORIES[editingExpense.category]?.label || editingExpense.category}</p>
                </div>
                
                {/* Status */}
                <div>
                  <Label>Status</Label>
                  <Select value={expenseForm.status} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="projetado">📋 Previsto</SelectItem>
                      <SelectItem value="pago">✅ Pago</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Valores Realizados */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Realizado *</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="0"
                      value={expenseForm.value}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, value: e.target.value }))}
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <Label>Quantidade Realizada</Label>
                    <Input
                      type="number"
                      min="1"
                      value={expenseForm.quantity}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, quantity: e.target.value }))}
                      placeholder="1"
                    />
                  </div>
                </div>
                
                {/* Forma de Pagamento - apenas quando status é "pago" */}
                {expenseForm.status === 'pago' && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <Label className="text-sm font-semibold mb-3 block text-green-800">Forma de Pagamento</Label>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs">Método</Label>
                        <Select value={expenseForm.payment_method} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, payment_method: v }))}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pix">💳 PIX</SelectItem>
                            <SelectItem value="cartao">💳 Cartão</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs">Data Pagamento</Label>
                        <Input
                          type="date"
                          value={expenseForm.payment_date || ''}
                          onChange={(e) => setExpenseForm(prev => ({ ...prev, payment_date: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Formulário completo para NOVA despesa ou edição de ORÇADO */
              <>
            <div>
              <Label>Descrição *</Label>
              <Input
                required
                value={expenseForm.description}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Ex: Seguro viagem"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Categoria</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPENSE_CATEGORIES).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Status *</Label>
                <Select value={expenseForm.status} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pago">💰 Pago</SelectItem>
                    <SelectItem value="projetado">📋 Projetado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de Custo *</Label>
                <Select value={expenseForm.cost_type} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, cost_type: v, value: 0, cost_per_person: 0 }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixo">📌 Custo Fixo</SelectItem>
                    <SelectItem value="variavel">📊 Custo Variável</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Data Pagamento</Label>
                <Input
                  type="date"
                  value={expenseForm.payment_date}
                  onChange={(e) => setExpenseForm(prev => ({ ...prev, payment_date: e.target.value }))}
                />
              </div>
            </div>
            
            {expenseForm.cost_type === 'fixo' ? (
              <>
                {/* Checkbox Multiplicar por Cliente - ANTES dos campos */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={expenseForm.varies_by_client || false}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, varies_by_client: e.target.checked }))}
                      className="w-4 h-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm font-medium text-purple-800">📊 Multiplicar por cliente</span>
                  </label>
                  <p className="text-xs text-purple-600 mt-1 ml-6">
                    {expenseForm.varies_by_client 
                      ? `O valor será multiplicado por ${actualPeople} clientes` 
                      : 'Se marcado, o valor será multiplicado pelo número de clientes'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor *</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="0"
                      value={expenseForm.value}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, value: parseFloat(e.target.value) || 0 }))}
                      placeholder="0,00"
                    />
                  </div>
                  <div>
                    <Label>Quantidade</Label>
                    <Input
                      type="number"
                      min="1"
                      value={expenseForm.quantity}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      placeholder="1"
                    />
                  </div>
                </div>

                {/* Guias isentos de hospedagem - apenas para categoria hospedagem */}
                {expenseForm.category === 'hospedagem' && (
                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <Label className="text-sm font-medium text-orange-800 mb-2 block">🏨 Guias Isentos de Hospedagem</Label>
                    <Select 
                      value={String(expenseForm.guides_exempt_lodging || 0)} 
                      onValueChange={(v) => setExpenseForm(prev => ({ ...prev, guides_exempt_lodging: parseInt(v) }))}
                    >
                      <SelectTrigger className="w-full bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Nenhum guia isento</SelectItem>
                        <SelectItem value="1">1 guia isento</SelectItem>
                        <SelectItem value="2">2 guias isentos</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-orange-600 mt-1">
                      Guias que não pagam hospedagem serão excluídos do cálculo
                    </p>
                  </div>
                )}

                {/* Fórmula e resultado */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600">Fórmula:</p>
                      <p className="font-medium text-blue-800">
                        {formatPrice(expenseForm.value || 0)} × {expenseForm.quantity || 1} qtd
                        {expenseForm.varies_by_client && ` × ${actualPeople} clientes`}
                        {expenseForm.category === 'hospedagem' && expenseForm.guides_exempt_lodging > 0 && 
                          ` - ${expenseForm.guides_exempt_lodging} guia(s) isento(s)`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-600">Valor Total:</p>
                      <p className="text-xl font-bold text-blue-800">
                        {formatPrice(
                          (expenseForm.value || 0) * (expenseForm.quantity || 1) * 
                          (expenseForm.varies_by_client 
                            ? Math.max(0, actualPeople - (expenseForm.category === 'hospedagem' ? (expenseForm.guides_exempt_lodging || 0) : 0))
                            : 1)
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* CUSTO VARIÁVEL - Multiplicar por cliente É AUTOMÁTICO */}
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-purple-800">📊 Multiplicado automaticamente por cliente</span>
                    <span className="px-2 py-0.5 text-xs bg-purple-200 text-purple-800 rounded">AUTOMÁTICO</span>
                  </div>
                  <p className="text-xs text-purple-600">
                    Custos variáveis são sempre multiplicados pelo número de clientes ({actualPeople})
                  </p>
                </div>
                
                {/* Incluir guias no custo variável */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <Label className="text-sm font-medium text-green-800 mb-2 block">👥 Incluir Guias no Custo</Label>
                  <Select 
                    value={String(expenseForm.include_guides || 0)} 
                    onValueChange={(v) => setExpenseForm(prev => ({ ...prev, include_guides: parseInt(v) }))}
                  >
                    <SelectTrigger className="w-full bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Nenhum guia (apenas clientes)</SelectItem>
                      <SelectItem value="1">+ 1 guia (viagens aéreas)</SelectItem>
                      <SelectItem value="2">+ 2 guias (viagens rodoviárias)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-green-600 mt-1">
                    Para custos como seguro, alimentação e taxas individuais que também se aplicam aos guias
                  </p>
                </div>

                {/* Custo Variável */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Valor Unitário *</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="0"
                      value={expenseForm.cost_per_person}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, cost_per_person: parseFloat(e.target.value) || 0 }))}
                      placeholder="Ex: 250"
                    />
                  </div>
                  <div>
                    <Label>Quantidade *</Label>
                    <Input
                      required
                      type="number"
                      min="1"
                      value={expenseForm.quantity}
                      onChange={(e) => setExpenseForm(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                      placeholder="Ex: 7"
                    />
                    <p className="text-xs text-gray-500 mt-1">Ex: dias, refeições</p>
                  </div>
                </div>
                
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-blue-600">Fórmula:</p>
                      <p className="font-medium text-blue-800">
                        {formatPrice(expenseForm.cost_per_person || 0)} × {expenseForm.quantity || 1} qtd
                        {` × (${actualPeople} clientes`}
                        {(expenseForm.include_guides || 0) > 0 && ` + ${expenseForm.include_guides} guia(s)`}
                        {`)  = ${actualPeople + (expenseForm.include_guides || 0)} pessoas`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-blue-600">Valor Total:</p>
                      <p className="text-xl font-bold text-blue-800">
                        {formatPrice(
                          (expenseForm.cost_per_person || 0) * (expenseForm.quantity || 1) * (actualPeople + (expenseForm.include_guides || 0))
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
            
            <div>
              <Label>Observações</Label>
              <Input
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Notas adicionais..."
              />
            </div>
            
            {/* Forma de Pagamento - apenas quando status é "pago" */}
            {expenseForm.status === 'pago' && (
              <div className="p-4 bg-gray-50 rounded-lg border">
                <Label className="text-sm font-semibold mb-3 block">Forma de Pagamento</Label>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Método</Label>
                    <Select value={expenseForm.payment_method} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, payment_method: v, card_installments: 1 }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pix">💳 PIX</SelectItem>
                        <SelectItem value="cartao">💳 Cartão</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {expenseForm.payment_method === 'cartao' && (
                    <div>
                      <Label className="text-xs">Tipo</Label>
                      <Select value={expenseForm.card_type} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, card_type: v, card_installments: v === 'avista' ? 1 : prev.card_installments }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="avista">À Vista</SelectItem>
                          <SelectItem value="prazo">A Prazo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                
                {expenseForm.payment_method === 'cartao' && expenseForm.card_type === 'prazo' && (
                  <div className="mt-3">
                    <Label className="text-xs">Parcelas</Label>
                    <Select value={String(expenseForm.card_installments)} onValueChange={(v) => setExpenseForm(prev => ({ ...prev, card_installments: parseInt(v) }))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}x</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {expenseForm.payment_method === 'cartao' && (
                  <div className="mt-3 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    <strong>📅 Pagamento no dia 28</strong>
                    {expenseForm.card_type === 'prazo' && expenseForm.card_installments > 1 && (
                      <span> - {expenseForm.card_installments}x de {formatPrice((expenseForm.cost_type === 'variavel' ? expenseForm.cost_per_person * expenseForm.quantity * actualPeople : expenseForm.value) / expenseForm.card_installments)}</span>
                    )}
                  </div>
                )}
              </div>
            )}
              </>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowExpenseModal(false); setEditMode('orcado'); }} className="flex-1">
                Cancelar
              </Button>
              <Button type="submit" className="flex-1 bg-[#003366] hover:bg-[#002244]">
                <Save className="w-4 h-4 mr-2" />
                {editingExpense ? 'Atualizar' : 'Adicionar'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Exclusão de Despesa Recorrente */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>🗑️ Excluir Despesa</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 mb-4">
            Esta despesa faz parte de uma série recorrente. O que deseja fazer?
          </p>
          <div className="flex flex-col gap-2">
            <Button 
              variant="outline"
              onClick={() => {
                handleDeleteExpense(deleteExpenseId, false);
              }}
            >
              Excluir apenas esta
            </Button>
            <Button 
              variant="destructive"
              onClick={() => {
                handleDeleteExpense(deleteExpenseId, true);
              }}
            >
              Excluir esta e futuras parcelas
            </Button>
            <Button 
              variant="ghost"
              onClick={() => { setShowDeleteModal(false); setDeleteExpenseId(null); }}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Modal */}
      <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Configurar Operação</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Quantidade Orçada de Pessoas *</Label>
              <Input
                type="number"
                min="0"
                value={statusForm.budget_people}
                onChange={(e) => setStatusForm(prev => ({ ...prev, budget_people: parseInt(e.target.value) || 0 }))}
                placeholder="Ex: 20"
              />
              <p className="text-xs text-gray-500 mt-1">Número de pessoas planejadas para essa operação</p>
            </div>
            
            <div>
              <Label>Status da Operação *</Label>
              <Select value={statusForm.status} onValueChange={(v) => setStatusForm(prev => ({ ...prev, status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="aguardando_data">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-600" />
                      Aguardando Data
                    </div>
                  </SelectItem>
                  <SelectItem value="realizado">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      Realizado
                    </div>
                  </SelectItem>
                  <SelectItem value="adiado">
                    <div className="flex items-center gap-2">
                      <CalendarX className="w-4 h-4 text-orange-600" />
                      Adiado
                    </div>
                  </SelectItem>
                  <SelectItem value="cancelado">
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      Cancelado
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              
              <div className="mt-2 p-3 bg-gray-50 rounded-lg text-xs text-gray-600">
                <p><strong>Realizado:</strong> Viagem concluída - considera números reais no DRE</p>
                <p><strong>Aguardando Data:</strong> Considera orçado se maior que vendas</p>
                <p><strong>Adiado:</strong> Altera a data do pacote automaticamente</p>
                <p><strong>Cancelado:</strong> Não contabiliza no DRE geral</p>
              </div>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowStatusModal(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={handleUpdateStatus} className="flex-1 bg-[#003366] hover:bg-[#002244]">
                <Save className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Postpone Modal with Calendar */}
      <Dialog open={showPostponeModal} onOpenChange={setShowPostponeModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adiar Operação</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-orange-800">
                <strong>⚠️ Atenção:</strong> Ao adiar, a data será alterada automaticamente em &quot;Gerenciar Pacotes&quot;.
              </p>
            </div>
            
            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-800">
                <strong>📦 Pacote:</strong> {operationDetails?.package_name}<br/>
                <strong>⏱️ Duração:</strong> {currentPackage?.duration_days || 0} dias
              </p>
            </div>
            
            <div>
              <Label>Nova Data de Início *</Label>
              <Input
                type="date"
                value={newStartDate}
                onChange={(e) => setNewStartDate(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Selecione a data de início da viagem</p>
            </div>
            
            {newStartDate && currentPackage?.duration_days && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-800">
                  <strong>📅 Novas Datas:</strong><br/>
                  {formatDateRange(newStartDate)}
                </p>
              </div>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setShowPostponeModal(false); setNewStartDate(''); }} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={handleConfirmPostpone} 
                disabled={!newStartDate}
                className="flex-1 bg-orange-600 hover:bg-orange-700"
              >
                <CalendarX className="w-4 h-4 mr-2" />
                Confirmar Adiamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal para Replicar Despesas */}
      <Dialog open={showReplicateModal} onOpenChange={setShowReplicateModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Replicar Despesas para Outras Datas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Deseja copiar as despesas desta operação para outras datas do mesmo pacote?
            </p>
            
            {replicateData.otherDates && replicateData.otherDates.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium">Datas disponíveis:</p>
                {replicateData.otherDates.map(d => (
                  <div key={d.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <input 
                      type="checkbox" 
                      id={`date-${d.id}`}
                      defaultChecked
                      className="rounded"
                    />
                    <label htmlFor={`date-${d.id}`} className="text-sm">{d.date}</label>
                  </div>
                ))}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => {
                setShowReplicateModal(false);
                setReplicateData({ sourceOpId: '', otherDates: [] });
              }}>
                Não, apenas esta data
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  const selectedDates = replicateData.otherDates
                    ?.filter(d => document.getElementById(`date-${d.id}`)?.checked)
                    .map(d => d.operationId) || [];
                  handleReplicateExpenses(selectedDates);
                }}
              >
                Sim, replicar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente auxiliar para ícone
const BarChart3 = ({ className }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v18h18"/>
    <path d="M18 17V9"/>
    <path d="M13 17V5"/>
    <path d="M8 17v-3"/>
  </svg>
);

export default AdminOperationalResults;
